/**
 * GPSUpdatePanel - Panneau de mise à jour GPS manuelle
 * Permet aux admins/opérateurs de mettre à jour la position d'un camion
 * et de simuler un trajet animé
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  MapPin, Navigation, Play, Pause, RotateCcw, 
  Crosshair, Search, Truck, Clock, Gauge
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GPSUpdatePanel = ({ operation, onLocationUpdate }) => {
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [address, setAddress] = useState('');
  const [note, setNote] = useState('');
  const [searching, setSearching] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  
  // Simulation state
  const [simulating, setSimulating] = useState(false);
  const [simulationProgress, setSimulationProgress] = useState(0);
  const simulationRef = useRef(null);

  // Initialiser avec la position actuelle si disponible
  useEffect(() => {
    if (operation?.lastKnownLocation) {
      setLatitude(operation.lastKnownLocation.latitude?.toString() || '');
      setLongitude(operation.lastKnownLocation.longitude?.toString() || '');
      setAddress(operation.lastKnownLocation.address || '');
    }
  }, [operation]);

  // Vérifier le statut de simulation au chargement
  useEffect(() => {
    checkSimulationStatus();
  }, [operation?.id]);

  const checkSimulationStatus = async () => {
    if (!operation?.id) return;
    try {
      const response = await axios.get(`${API}/tracking/${operation.id}/simulation-status`);
      if (response.data.simulationActive) {
        setSimulating(true);
        setSimulationProgress(response.data.currentLocation?.progress || 0);
        startSimulationLoop();
      }
    } catch (error) {
      console.error('Error checking simulation status:', error);
    }
  };

  // Recherche d'adresse avec Nominatim
  const searchAddress = async () => {
    if (!address.trim()) return;
    
    setSearching(true);
    setShowResults(true);
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&countrycodes=ci&limit=5`
      );
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de rechercher l'adresse",
        variant: "destructive"
      });
    } finally {
      setSearching(false);
    }
  };

  // Sélectionner un résultat de recherche
  const selectAddress = (result) => {
    setLatitude(result.lat);
    setLongitude(result.lon);
    setAddress(result.display_name);
    setShowResults(false);
    setSearchResults([]);
  };

  // Mettre à jour la position manuellement
  const updateLocation = async () => {
    if (!latitude || !longitude) {
      toast({
        title: "Erreur",
        description: "Veuillez entrer les coordonnées GPS",
        variant: "destructive"
      });
      return;
    }

    setUpdating(true);
    try {
      const response = await axios.put(`${API}/tracking/${operation.id}/location`, {
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
        address: address || null,
        note: note || null
      });

      toast({
        title: "Position mise à jour",
        description: "La nouvelle position a été enregistrée"
      });

      if (onLocationUpdate) {
        onLocationUpdate(response.data.location);
      }

      setNote('');
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de mettre à jour la position",
        variant: "destructive"
      });
    } finally {
      setUpdating(false);
    }
  };

  // Démarrer la simulation
  const startSimulation = async () => {
    try {
      await axios.post(`${API}/tracking/${operation.id}/simulate`, {
        start: true,
        interval_seconds: 5
      });

      setSimulating(true);
      setSimulationProgress(0);
      
      toast({
        title: "Simulation démarrée",
        description: "Le camion va se déplacer automatiquement toutes les 5 secondes"
      });

      startSimulationLoop();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de démarrer la simulation",
        variant: "destructive"
      });
    }
  };

  // Boucle de simulation
  const startSimulationLoop = useCallback(() => {
    if (simulationRef.current) {
      clearInterval(simulationRef.current);
    }

    simulationRef.current = setInterval(async () => {
      try {
        const response = await axios.post(`${API}/tracking/${operation.id}/simulate/next`);
        
        if (response.data.completed) {
          clearInterval(simulationRef.current);
          setSimulating(false);
          setSimulationProgress(100);
          
          toast({
            title: "Simulation terminée",
            description: "Le camion est arrivé à destination"
          });
        } else {
          setSimulationProgress(response.data.progress);
          setLatitude(response.data.location.latitude.toString());
          setLongitude(response.data.location.longitude.toString());
          
          if (onLocationUpdate) {
            onLocationUpdate(response.data.location);
          }
        }
      } catch (error) {
        console.error('Simulation step error:', error);
      }
    }, 5000);
  }, [operation?.id, onLocationUpdate]);

  // Arrêter la simulation
  const stopSimulation = async () => {
    if (simulationRef.current) {
      clearInterval(simulationRef.current);
    }

    try {
      await axios.post(`${API}/tracking/${operation.id}/simulate`, {
        start: false
      });

      setSimulating(false);
      
      toast({
        title: "Simulation arrêtée",
        description: "Le camion ne se déplace plus automatiquement"
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible d'arrêter la simulation",
        variant: "destructive"
      });
    }
  };

  // Réinitialiser au point de départ
  const resetToStart = async () => {
    if (!operation?.departureCoordinates) {
      toast({
        title: "Erreur",
        description: "Coordonnées de départ non disponibles",
        variant: "destructive"
      });
      return;
    }

    const depCoords = operation.departureCoordinates;
    const lat = depCoords.lat || depCoords.latitude;
    const lng = depCoords.lng || depCoords.longitude;

    setLatitude(lat.toString());
    setLongitude(lng.toString());
    setAddress(operation.departure || '');

    await updateLocationDirect(lat, lng, operation.departure);
  };

  const updateLocationDirect = async (lat, lng, addr) => {
    try {
      const response = await axios.put(`${API}/tracking/${operation.id}/location`, {
        latitude: lat,
        longitude: lng,
        address: addr,
        note: "Réinitialisé au départ"
      });

      if (onLocationUpdate) {
        onLocationUpdate(response.data.location);
      }

      toast({
        title: "Position réinitialisée",
        description: "Le camion est maintenant au point de départ"
      });
    } catch (error) {
      console.error('Error resetting location:', error);
    }
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (simulationRef.current) {
        clearInterval(simulationRef.current);
      }
    };
  }, []);

  // Vérifier si on peut simuler (coordonnées départ/arrivée requises)
  // Support des différents formats de coordonnées: {latitude, longitude} ou {lat, lng}
  const hasValidCoords = (coords) => {
    if (!coords) return false;
    return (coords.latitude && coords.longitude) || (coords.lat && coords.lng);
  };
  const canSimulate = hasValidCoords(operation?.departureCoordinates) && hasValidCoords(operation?.arrivalCoordinates);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Navigation className="w-4 h-4 text-emerald-600" />
            Mise à jour GPS
          </div>
          {operation?.lastKnownLocation && (
            <Badge variant="outline" className="text-xs">
              <Clock className="w-3 h-3 mr-1" />
              {new Date(operation.lastKnownLocation.timestamp).toLocaleTimeString('fr-FR')}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Position actuelle */}
        {operation?.lastKnownLocation && (
          <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="flex items-center gap-2 text-sm text-emerald-700 mb-1">
              <Truck className="w-4 h-4" />
              <span className="font-medium">Position actuelle</span>
              {operation.lastKnownLocation.source === 'simulation' && (
                <Badge className="bg-blue-100 text-blue-700 text-xs">Simulation</Badge>
              )}
            </div>
            <div className="text-xs text-emerald-600">
              {operation.lastKnownLocation.latitude?.toFixed(6)}, {operation.lastKnownLocation.longitude?.toFixed(6)}
              {operation.lastKnownLocation.speed > 0 && (
                <span className="ml-2">
                  <Gauge className="w-3 h-3 inline mr-1" />
                  {Math.round(operation.lastKnownLocation.speed)} km/h
                </span>
              )}
            </div>
            {operation.lastKnownLocation.address && (
              <div className="text-xs text-emerald-600 mt-1 truncate">
                📍 {operation.lastKnownLocation.address}
              </div>
            )}
          </div>
        )}

        {/* Recherche d'adresse */}
        <div className="space-y-2">
          <Label className="text-xs">Rechercher une adresse</Label>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Input
                placeholder="Ex: Plateau, Abidjan"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchAddress()}
                className="pr-8"
              />
              {showResults && searchResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {searchResults.map((result, idx) => (
                    <button
                      key={idx}
                      onClick={() => selectAddress(result)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 border-b last:border-b-0"
                    >
                      <div className="font-medium truncate">{result.display_name.split(',')[0]}</div>
                      <div className="text-xs text-gray-500 truncate">{result.display_name}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={searchAddress}
              disabled={searching}
            >
              <Search className={`w-4 h-4 ${searching ? 'animate-pulse' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Coordonnées GPS */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Latitude</Label>
            <Input
              type="number"
              step="0.000001"
              placeholder="5.345678"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Longitude</Label>
            <Input
              type="number"
              step="0.000001"
              placeholder="-4.012345"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
            />
          </div>
        </div>

        {/* Note optionnelle */}
        <div className="space-y-1">
          <Label className="text-xs">Note (optionnel)</Label>
          <Input
            placeholder="Ex: Arrêt pause déjeuner"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        {/* Boutons d'action */}
        <div className="flex gap-2">
          <Button
            onClick={updateLocation}
            disabled={updating || !latitude || !longitude}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
          >
            <MapPin className="w-4 h-4 mr-2" />
            {updating ? 'Mise à jour...' : 'Mettre à jour'}
          </Button>
          <Button
            variant="outline"
            onClick={resetToStart}
            disabled={!operation?.departureCoordinates}
            title="Réinitialiser au départ"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>

        {/* Section Simulation */}
        {canSimulate && (
          <div className="pt-3 border-t">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-xs font-medium">Simulation de trajet</Label>
              {simulating && (
                <Badge className="bg-blue-100 text-blue-700">
                  {simulationProgress.toFixed(0)}% complété
                </Badge>
              )}
            </div>
            
            {simulating && (
              <div className="mb-3">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 transition-all duration-500"
                    style={{ width: `${simulationProgress}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>{operation.departure}</span>
                  <span>{operation.arrival}</span>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              {!simulating ? (
                <Button
                  onClick={startSimulation}
                  variant="outline"
                  className="flex-1 border-blue-200 text-blue-700 hover:bg-blue-50"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Démarrer simulation
                </Button>
              ) : (
                <Button
                  onClick={stopSimulation}
                  variant="outline"
                  className="flex-1 border-orange-200 text-orange-700 hover:bg-orange-50"
                >
                  <Pause className="w-4 h-4 mr-2" />
                  Arrêter
                </Button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              La simulation déplace le camion le long du trajet toutes les 5 secondes.
            </p>
          </div>
        )}

        {!canSimulate && (
          <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
            ⚠️ Coordonnées de départ ou d'arrivée manquantes. La simulation n'est pas disponible.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default GPSUpdatePanel;
